// frontend/src/pages/ParceriasPage.jsx
import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';

const ParceriasPage = () => {
  const { currentUser } = useAuth();
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parceiroSelecionado, setParceiroSelecionado] = useState(null);

  const encerrarParceria = async (id) => {
    const ok = window.confirm('Tem certeza que deseja encerrar essa parceria?');
    if (!ok) return;
    try {
      await updateDoc(doc(db, 'parcerias', id), { status: 'ended' });
      setParceiros((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error('Erro ao encerrar parceria:', e);
      alert('Não foi possível encerrar a parceria. Tente novamente.');
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const buscarParcerias = async () => {
      try {
        setLoading(true);

        // 🔎 Compatível com os dois padrões de campos:
        // novo:  criador / convidado
        // antigo: from / to
        const qA1 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('criador', '==', currentUser.uid)
        );
        const qA2 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('convidado', '==', currentUser.uid)
        );
        const qB1 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('from', '==', currentUser.uid)
        );
        const qB2 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('to', '==', currentUser.uid)
        );

        const [sA1, sA2, sB1, sB2] = await Promise.all([
          getDocs(qA1),
          getDocs(qA2),
          getDocs(qB1),
          getDocs(qB2),
        ]);

        // mescla e remove duplicatas por id
        const seen = new Set();
        const allDocs = [
          ...sA1.docs,
          ...sA2.docs,
          ...sB1.docs,
          ...sB2.docs,
        ].filter((d) => (seen.has(d.id) ? false : seen.add(d.id)));

        const lista = await Promise.all(
          allDocs.map(async (docSnap) => {
            const data = docSnap.data();
            const criador = data.criador ?? data.from;
            const convidado = data.convidado ?? data.to;

            const parceiroId =
              criador === currentUser.uid ? convidado : criador;

            // pega os dados do parceiro diretamente pelo id
            const userSnap = await getDoc(doc(db, 'users', parceiroId));
            const u = userSnap.exists() ? userSnap.data() : {};

            return {
              id: docSnap.id,
              parceiroId,
              nome: u?.nome || 'Sem nome',
              email: u?.email || 'Sem e-mail',
              foto: u?.fotoPerfil?.url || u?.fotoPerfil || null,
            };
          })
        );

        setParceiros(lista);
      } catch (error) {
        console.error('Erro ao buscar parcerias:', error);
      } finally {
        setLoading(false);
      }
    };

    buscarParcerias();
  }, [currentUser?.uid]);

  // ✅ Spinner enquanto o usuário ainda não carregou
  if (!currentUser?.uid) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
        <p>Carregando dados do usuário...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Minhas Parcerias</h1>

      {loading ? (
        <p>Carregando parcerias...</p>
      ) : parceiros.length === 0 ? (
        <p>Você ainda não possui parcerias ativas.</p>
      ) : (
        <ul className="space-y-4">
          {parceiros.map((p) => (
            <li
              key={p.id}
              className="bg-white shadow p-4 rounded flex justify-between items-center"
            >
              <div className="flex items-center gap-3">
                <img
                  src={p.foto || '/placeholder-user.jpg'}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold">{p.nome}</p>
                  <p className="text-sm text-gray-600">{p.email}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/perfil/${p.parceiroId}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Ver perfil
                </Link>
                <button
                  onClick={() => setParceiroSelecionado(p)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Compartilhar imóvel
                </button>
                <button
                  onClick={() => encerrarParceria(p.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Encerrar parceria
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Modal de compartilhamento (placeholder) */}
      {parceiroSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              Compartilhar imóvel com {parceiroSelecionado.nome}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Aqui entrará a lista dos seus imóveis para selecionar e
              compartilhar.
            </p>

            {/* TODO: listar imóveis e confirmar compartilhamento */}

            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-gray-700 hover:underline"
                onClick={() => setParceiroSelecionado(null)}
              >
                Cancelar
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Compartilhar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParceriasPage;
